// ── Touch Buttons ──
// On-screen action buttons for mobile: Fire, ADS, Reload, Jump, Crouch, WeaponSwitch.

import { loadSavedLayout, loadHiddenButtons } from '../ui/hud-layout-editor';
import { settingsStore, type ControlMode } from '../core/settings';

export interface TouchButtonState {
  fire: boolean;
  ads: boolean;
  reload: boolean;
  jump: boolean;
  crouch: boolean;
  weaponSwitch: boolean;
  /** C6: Push-to-talk (voice chat) — mobile analog of the CapsLock key on desktop. */
  ptt: boolean;
  /** MOB-C2: Toggle scope/zoom for AUG/SG553/AWP — mobile analog of the desktop scope toggle. */
  scope: boolean;
  /** MOB-C3: Interact / plant / defuse — mobile analog of the desktop E key. */
  interact: boolean;
  /** F-MOB-H3: Open buy menu (mobile analog of the desktop B key). */
  buy: boolean;
  /** F-MOB-H3: Toggle scoreboard (mobile analog of Tab). */
  scoreboard: boolean;
  /** F-MOB-H3: Cycle equipped grenade (mobile analog of G). */
  grenade: boolean;
  /** F-MOB-H3: Open ping wheel (mobile analog of the ping keybind). */
  ping: boolean;
  /** F-MOB-H3 (chat): Open the chat composer overlay (mobile analog of Y/U). */
  chat: boolean;
  /** MOB-W6: Quick-buy slot 1 — fires the user-chosen weapon for slot 0. */
  quickbuy1: boolean;
  /** MOB-W6: Quick-buy slot 2 — fires the user-chosen weapon for slot 1. */
  quickbuy2: boolean;
  /** MOB-W6: Quick-buy slot 3 — fires the user-chosen weapon for slot 2. */
  quickbuy3: boolean;
  /** F-GP-006: Inspect weapon (mobile analog of the desktop inspect keybind, default F). */
  inspect: boolean;
  /** F-GP-006: Drop currently-equipped weapon (mobile analog of the desktop drop keybind, default G). */
  drop: boolean;
  /** BUG-MT-7: Open the casual map-vote panel (mobile analog of the desktop map-vote keybind). */
  mapvote: boolean;
  /** BUG-PARITY-H2: Open the radio command wheel (mobile analog of the desktop Z/X/C radio submenus). */
  radio: boolean;
}

export interface ButtonDef {
  id: string;
  label: string;
  size: number;
  /** CSS positioning values (percentage-based). */
  right?: string;
  left?: string;
  bottom?: string;
  top?: string;
  key: keyof TouchButtonState;
  /** F-MOB-M3: which control modes show this button. Default: ['simple','advanced'].
   *  Simple mode trims to FIRE/AIM/RELOAD/JUMP/CROUCH; Advanced shows everything. */
  modes?: Array<'simple' | 'advanced'>;
}

export const BUTTON_DEFS: ButtonDef[] = [
  // F-MOB-M3: simple mode keeps the core five (fire / ads / reload / jump / crouch).
  // Advanced mode adds weapon-switch, ptt, scope, interact, buy, scoreboard, grenade-cycle, ping.
  { id: 'btn-fire',    label: 'FIRE',   size: 64, right: '8%',  bottom: '20%', key: 'fire',         modes: ['simple', 'advanced'] },
  { id: 'btn-ads',     label: 'ADS',    size: 48, right: '8%',  bottom: '40%', key: 'ads',          modes: ['simple', 'advanced'] },
  // MOB-ERG: RELOAD shifted down to bottom: 50% so it no longer collides with SCOPE
  // (which now sits at bottom: 62%).
  { id: 'btn-reload',  label: 'R',      size: 44, right: '8%',  bottom: '50%', key: 'reload',       modes: ['simple', 'advanced'] },
  // MOB-ERG: JUMP/CROUCH lifted out of the joystick zone (left 0-40%, bottom 0-50%).
  // bottom: 24% places them just above the joystick capture rectangle.
  { id: 'btn-jump',    label: 'JUMP',   size: 48, left: '24%',  bottom: '24%', key: 'jump',         modes: ['simple', 'advanced'] },
  { id: 'btn-crouch',  label: 'CROUCH', size: 48, left: '40%',  bottom: '24%', key: 'crouch',       modes: ['simple', 'advanced'] },
  { id: 'btn-weapon',  label: 'WPN',    size: 44, right: '3%',  bottom: '70%', key: 'weaponSwitch', modes: ['advanced'] },
  // C6: Push-to-talk on left side above the joystick zone, clear of movement fingers.
  // MOB-ERG: tightened to left: 3% to give USE breathing room and align with the safe edge.
  // BUG-MT-14: raised bottom 30% → 52% so PTT sits ABOVE the joystick capture
  // rectangle (left 0-40%, bottom 0-50%). A joystick drag that crosses the
  // joystick zone boundary into PTT would otherwise steer movement.
  { id: 'btn-ptt',     label: 'PTT',    size: 48, left: '3%',   bottom: '52%', key: 'ptt',          modes: ['advanced'] },
  // MOB-C2 + MOB-ERG: Scope toggle on the right thumb side, separated from RELOAD by
  // pushing it up to bottom: 62% (RELOAD at 50%) so the two buttons no longer overlap.
  { id: 'btn-scope',   label: 'SCOPE',  size: 44, right: '3%',  bottom: '62%', key: 'scope',        modes: ['advanced'] },
  // MOB-C3: Interact / plant / defuse on left side, paired with PTT above the joystick clear zone.
  // BUG-MT-14: raised bottom 46% → 60% so USE sits ABOVE the joystick capture
  // rectangle (left 0-40%, bottom 0-50%). USE shares its bottom band with the
  // quick-buy row, but Q1-Q3 are only visible during freeze-time (when USE is
  // not needed) and USE is only useful during the active round (when Q1-Q3 are
  // hidden), so the time-multiplexing keeps them visually disjoint in practice.
  { id: 'btn-interact',label: 'USE',    size: 48, left: '8%',   bottom: '60%', key: 'interact',     modes: ['advanced'] },
  // F-MOB-H3: Buy menu — top-left of HUD, sized small so it doesn't clutter the play area.
  { id: 'btn-buy',     label: 'BUY',    size: 40, left: '3%',   top: '12%',    key: 'buy',          modes: ['advanced'] },
  // F-MOB-H3: Scoreboard toggle — top-right next to kill feed.
  { id: 'btn-scoreboard', label: 'TAB', size: 40, right: '3%',  top: '12%',    key: 'scoreboard',   modes: ['advanced'] },
  // F-MOB-H3: Grenade cycle — right edge, above WPN.
  { id: 'btn-grenade', label: 'GRN',    size: 40, right: '3%',  bottom: '82%', key: 'grenade',      modes: ['advanced'] },
  // F-MOB-H3: Ping wheel — left edge, above USE.
  // BUG-MT-15: moved left 8% → 3% and bottom 60% → 75% to clear the freeze-time
  // quick-buy row (Q1 at left 10% bottom 60% was directly overlapping PING).
  // New spot keeps PING on the safe left edge column with PTT (bottom 52%) and
  // BUY (top 12%), and stays well clear of USE (bottom 60%).
  { id: 'btn-ping',    label: 'PING',   size: 40, left: '3%',   bottom: '75%', key: 'ping',         modes: ['advanced'] },
  // F-MOB-H3 (bomb): Plant / defuse button — reuses the `interact` (USE) input
  // key so a press routes to the same server message as the desktop E key. The
  // button is hidden by default and revealed via setBombVisible(true) when the
  // local snapshot reports HasBomb=true (carrier path). Position: bottom-right
  // of the play area, between FIRE and ADS so the dominant-hand thumb can find
  // it without leaving the action cluster. Uses a vivid red label so it stands
  // out from the neutral action buttons.
  { id: 'btn-bomb',    label: 'BOMB',   size: 56, right: '20%', bottom: '20%', key: 'interact',     modes: ['simple', 'advanced'] },
  // F-MOB-H3 (chat): Open the chat composer overlay (mobile analog of Y/U).
  // Top-right next to TAB / kill feed; small footprint so it doesn't crowd
  // the play area.
  { id: 'btn-chat',    label: 'CHAT',   size: 40, right: '13%', top: '12%',    key: 'chat',         modes: ['advanced'] },
  // MOB-W6: Three customizable quick-buy buttons. Visible only during the
  // freeze-time window (round start) and only when the player is in a
  // buy-allowed state — game.ts drives setQuickBuyVisible. Stacked
  // horizontally above the joystick / left-side action cluster so they
  // sit in a row the player can tap quickly without leaving the buy
  // window. Labels are updated dynamically by the host via
  // setQuickBuyLabel() once the loadout resolves.
  { id: 'btn-quickbuy-1', label: 'Q1', size: 56, left: '10%', bottom: '60%', key: 'quickbuy1', modes: ['simple', 'advanced'] },
  { id: 'btn-quickbuy-2', label: 'Q2', size: 56, left: '24%', bottom: '60%', key: 'quickbuy2', modes: ['simple', 'advanced'] },
  { id: 'btn-quickbuy-3', label: 'Q3', size: 56, left: '38%', bottom: '60%', key: 'quickbuy3', modes: ['simple', 'advanced'] },
  // F-GP-006: Inspect / drop. Advanced mode only (parity with desktop bindings F/G).
  // Stacked above WPN/SCOPE on the right edge so they don't crowd the action cluster.
  { id: 'btn-inspect', label: 'INSP', size: 40, right: '3%',  bottom: '78%', key: 'inspect',     modes: ['advanced'] },
  { id: 'btn-drop',    label: 'DROP', size: 40, right: '13%', bottom: '78%', key: 'drop',         modes: ['advanced'] },
  // BUG-MT-7: Map-vote button — top row alongside TAB / CHAT. Advanced mode only
  // because casual map-vote is a slow, deliberate action that does not belong in
  // simple mode's stripped-down core. Triggers an onMapVote callback on press
  // (TouchInputProvider routes it to mapVotePanel.toggle()).
  { id: 'btn-mapvote', label: 'VOTE', size: 40, right: '25%', top: '12%',    key: 'mapvote',      modes: ['advanced'] },
  // BUG-PARITY-H2: Radio command wheel — top row to the LEFT of VOTE/CHAT/TAB,
  // continuing the established ~12% spacing on the right side (TAB:3%, CHAT:13%,
  // VOTE:25%, RAD:37%). User spec proposed right:20% but that would put the 40px
  // button only 5% from VOTE and 7% from CHAT, which overlaps on narrow mobile
  // viewports — shifted to 37% to preserve the existing inter-button spacing.
  // Advanced mode only because radio callouts are a tactical comms feature that
  // does not belong in simple mode's stripped-down core. Triggers an onRadio
  // callback on press (TouchInputProvider routes it to the radio wheel UI,
  // mirroring the desktop Z/X/C submenus).
  { id: 'btn-radio',   label: 'RAD',  size: 40, right: '37%', top: '12%',    key: 'radio',        modes: ['advanced'] },
];

export class TouchButtons {
  private container: HTMLDivElement | null = null;
  private state: TouchButtonState = {
    fire: false,
    ads: false,
    reload: false,
    jump: false,
    crouch: false,
    weaponSwitch: false,
    ptt: false,
    scope: false,
    interact: false,
    buy: false,
    scoreboard: false,
    grenade: false,
    ping: false,
    chat: false,
    quickbuy1: false,
    quickbuy2: false,
    quickbuy3: false,
    inspect: false,
    drop: false,
    mapvote: false,
    radio: false,
  };

  /** Map from button element to its state key. */
  private buttonMap = new Map<HTMLElement, keyof TouchButtonState>();

  /** BUG-MT-7: rising-edge callback for the map-vote button.
   *  TouchInputProvider attaches via setOnMapVote(); pressed inside onTouchStart
   *  so the callback fires exactly once per tap (matching the pattern of other
   *  on-press callbacks routed through the provider). */
  onMapVote: (() => void) | null = null;

  /** BUG-PARITY-H2: rising-edge callback for the radio command wheel button.
   *  TouchInputProvider attaches via setOnRadio(); fired inside onTouchStart so
   *  a single tap invokes the callback exactly once (same pattern as
   *  onMapVote). Mobile analog of the desktop Z/X/C radio submenus. */
  onRadio: (() => void) | null = null;

  /** BUG-MT-16: snapshot of inline px sizes captured immediately before
   *  applyControlMode('simple') enlarges any button. Keyed by button id so the
   *  advanced-restore path can return to the user's customized size rather
   *  than falling back to BUTTON_DEFS.size (which loses HUD layout editor
   *  customizations after a simple→advanced cycle). */
  private userCustomSizes = new Map<string, number>();

  /** Track active touches per button. */
  private activeTouches = new Map<number, keyof TouchButtonState>();

  /** F-MOB-H3 (bomb): whether the local player is currently the bomb carrier.
   *  Drives conditional visibility of `#btn-bomb`. Defaults to false so the
   *  button starts hidden until a snapshot updates it. */
  private bombVisible = false;

  /** MOB-W6: whether the freeze-time quick-buy buttons should currently be
   *  visible. Driven by the host (round phase + buy-allowed gate). Defaults
   *  to false so the buttons stay hidden until the first freeze-time tick. */
  private quickBuyVisible = false;

  /** BUG-MT-27: guard against re-entrant construction. The touch overlay has
   *  a brief race on first join where the constructor path can run twice
   *  before the first paint (caller wires the input layer, then a snapshot
   *  arrives and triggers a re-init). Without a guard the second pass would
   *  rebuild the container, hide the first one, and produce a visible
   *  flicker. Set to true at the end of the successful constructor body. */
  private initialized = false;

  /** BUG-MT-27: tracks whether the first show()/hide() call has landed. The
   *  first call is deferred via requestAnimationFrame so the initial
   *  visibility is committed in the same paint as the freshly-mounted DOM,
   *  avoiding a one-frame "visible then hidden" flash on join. Subsequent
   *  calls apply synchronously. */
  private firstVisibilityApplied = false;

  // Bound handlers — BUG-MT-27: `!` definite-assignment assertions because
  // the constructor's `if (this.initialized) return` guard would otherwise
  // make TS conclude these readonly fields may stay unset. They are still
  // assigned exactly once before any side-effect that uses them.
  private readonly handleStart!: (e: TouchEvent) => void;
  private readonly handleEnd!: (e: TouchEvent) => void;
  private readonly handleVisibilityChange!: () => void;
  /** BUG-MT-19: pagehide is the iOS Safari BFCache equivalent of
   *  visibilitychange — restoring from BFCache will not fire visibilitychange
   *  but will fire pagehide on suspend, so touches left "held" during a
   *  BFCache freeze need this hook to clear in lockstep with the visibility
   *  path. */
  private readonly handlePageHide!: () => void;

  constructor() {
    // BUG-MT-27: re-entrant construction guard. If this instance has already
    // completed the construction path, return early so a second wiring pass
    // does not rebuild the container, re-attach listeners, and cause a
    // visible flicker on first join. Per-field defaults already initialize
    // the bound handlers as readonly so the second pass would have failed
    // anyway, but the explicit guard keeps the intent obvious.
    if (this.initialized) return;
    this.handleStart = this.onTouchStart.bind(this);
    this.handleEnd = this.onTouchEnd.bind(this);
    this.handleVisibilityChange = () => this.clearAllTouches();
    this.handlePageHide = () => this.clearAllTouches();
    this.createButtons();
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    // BUG-MT-19: pagehide also clears touches so iOS Safari BFCache restores
    // do not return with stuck PTT/FIRE/etc. state. Some test stub-DOMs lack
    // addEventListener — guard so this stays safe in vitest.
    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
      window.addEventListener('pagehide', this.handlePageHide);
    }
    // F-MOB-M3: apply current control mode (simple vs advanced) to the
    // freshly-created buttons so simple mode hides advanced-only entries
    // and bumps remaining buttons to a larger hit area.
    try {
      const mode = settingsStore.get().controlMode;
      this.applyControlMode(mode);
    } catch {
      // settings not yet initialized — defaults already render the advanced layout.
    }
    // BUG-MT-27: mark construction complete only after every side-effect has
    // succeeded so a thrown exception earlier in the body still leaves us
    // re-runnable on a retry.
    this.initialized = true;
  }

  /** MOB-L4: inject CSS once for the .touch-button-pressed press state.
   *  Idempotent — the style tag has a stable id so subsequent button
   *  constructions don't duplicate it. Uses transition + transform so the
   *  feedback is visible without per-frame work. Guards every DOM call so
   *  it is safe under stub-DOM test environments. */
  private static injectPressStyle(): void {
    if (typeof document === 'undefined') return;
    const doc = document as unknown as {
      getElementById?: (id: string) => unknown;
      createElement?: (t: string) => HTMLStyleElement;
      head?: { appendChild?: (n: unknown) => void };
    };
    if (typeof doc.getElementById !== 'function') return;
    if (typeof doc.createElement !== 'function') return;
    if (doc.getElementById('touch-button-press-style')) return;
    const style = doc.createElement('style');
    style.id = 'touch-button-press-style';
    style.textContent = `
      .touch-button-press {
        transition: transform 80ms ease-out, filter 80ms ease-out, opacity 80ms ease-out, box-shadow 80ms ease-out, background 80ms ease-out;
      }
      .touch-button-press.touch-button-pressed {
        transform: scale(0.9);
        filter: brightness(1.5);
        opacity: 1 !important;
      }
      /* BUG-MT-30: clear visual feedback while PTT audio is going out. Red
         glow + slight scale-up so the player sees the difference between
         "I'm holding PTT" (press state) and "my voice is actually
         transmitting" (VAD-confirmed). */
      .touch-button-press.ptt-transmitting {
        background: rgba(220, 38, 38, 0.7) !important;
        border-color: rgba(248, 113, 113, 0.95) !important;
        box-shadow: 0 0 14px 2px rgba(239, 68, 68, 0.85);
        transform: scale(1.05);
        opacity: 1 !important;
      }
    `;
    doc.head?.appendChild?.(style);
  }

  private createButtons(): void {
    TouchButtons.injectPressStyle();
    this.container = document.createElement('div');
    this.container.id = 'touch-buttons-overlay';
    Object.assign(this.container.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      pointerEvents: 'none',
      zIndex: '1001',
    } satisfies Partial<CSSStyleDeclaration>);

    for (const def of BUTTON_DEFS) {
      const btn = document.createElement('div');
      btn.id = def.id;
      btn.textContent = def.label;

      // MOB-ERG: wrap positional offsets with env(safe-area-inset-*) so JUMP/CROUCH
      // and the right-side action buttons don't overlap the iOS home-indicator
      // gesture or notch areas. Falls back to 0px on browsers that ignore env().
      Object.assign(btn.style, {
        position: 'absolute',
        width: `${def.size}px`,
        height: `${def.size}px`,
        borderRadius: '50%',
        background: 'rgba(30, 30, 30, 0.6)',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: def.size < 48 ? '10px' : '12px',
        fontWeight: 'bold',
        fontFamily: 'sans-serif',
        userSelect: 'none',
        touchAction: 'none',
        pointerEvents: 'auto',
        opacity: '0.6',
        border: '2px solid rgba(255, 255, 255, 0.3)',
        ...(def.right !== undefined ? { right: `calc(${def.right} + env(safe-area-inset-right, 0px))` } : {}),
        ...(def.left !== undefined ? { left: `calc(${def.left} + env(safe-area-inset-left, 0px))` } : {}),
        ...(def.bottom !== undefined ? { bottom: `calc(${def.bottom} + env(safe-area-inset-bottom, 0px))` } : {}),
        ...(def.top !== undefined ? { top: `calc(${def.top} + env(safe-area-inset-top, 0px))` } : {}),
      });

      // MOB-L4: tag every button with the press class so the injected
      // CSS owns the visible press state. classList toggling in onTouchStart/End
      // handles the actual transition. classList is guarded for stub-DOM tests.
      btn.classList?.add?.('touch-button-press');

      btn.addEventListener('touchstart', this.handleStart, { passive: false });
      btn.addEventListener('touchend', this.handleEnd);
      btn.addEventListener('touchcancel', this.handleEnd);

      this.buttonMap.set(btn, def.key);
      this.container.appendChild(btn);
    }

    document.body.appendChild(this.container);

    // HUD-PERSIST: apply any saved layout from HudLayoutEditor on top of the
    // BUTTON_DEFS defaults. The editor stores raw left/top percentages and a
    // pixel size per button; we override those inline styles directly (no
    // safe-area-inset wrapping) because the user positioned the button
    // explicitly and any inset offset would shift it away from the chosen
    // spot. Buttons absent from the saved layout keep their BUTTON_DEFS-driven
    // styles (with safe-area-inset wrapping) untouched.
    const saved = loadSavedLayout();
    if (saved) {
      for (const [btn] of this.buttonMap) {
        const entry = saved[btn.id];
        if (!entry) continue;
        btn.style.left = `${entry.x}%`;
        btn.style.top = `${entry.y}%`;
        btn.style.right = '';
        btn.style.bottom = '';
        btn.style.width = `${entry.size}px`;
        btn.style.height = `${entry.size}px`;
      }
    }
    // F-MOB-M5 / MOB-W10: respect persisted per-button visibility — buttons
    // the user toggled off in the layout editor are display:none until they
    // re-enable them.
    const hidden = loadHiddenButtons();
    if (hidden.size > 0) {
      for (const [btn] of this.buttonMap) {
        if (hidden.has(btn.id)) btn.style.display = 'none';
      }
    }

    // F-MOB-H3 (bomb): the BOMB button is hidden by default and only revealed
    // while the local player carries the bomb (or is in defuse range). Drive
    // the initial display:none here so the button never flashes on round
    // start before the first snapshot lands.
    this.applyBombVisibility();

    // MOB-W6: quick-buy buttons start hidden until the host calls
    // setQuickBuyVisible(true) at freeze-time. Same flash-prevention pattern
    // as the bomb button.
    this.applyQuickBuyVisibility();
  }

  /** F-MOB-H3 (bomb): apply the current bombVisible flag to the #btn-bomb
   *  element's display style. Pulled out of setBombVisible so the same logic
   *  can be invoked from createButtons() and applyControlMode(). */
  private applyBombVisibility(): void {
    for (const [btn] of this.buttonMap) {
      if (btn.id !== 'btn-bomb') continue;
      btn.style.display = this.bombVisible ? '' : 'none';
    }
  }

  /** F-MOB-H3 (bomb): toggle the BOMB button visibility based on the local
   *  player's HasBomb (isBombCarrier) state. Call from the snapshot handler
   *  whenever the carrier flag changes. Idempotent — safe to call every
   *  frame. */
  setBombVisible(visible: boolean): void {
    this.bombVisible = visible;
    this.applyBombVisibility();
  }

  /** MOB-W6: apply the quickBuyVisible flag to the three #btn-quickbuy-*
   *  elements' display style, matching the bomb-button pattern. */
  private applyQuickBuyVisibility(): void {
    for (const [btn] of this.buttonMap) {
      if (!btn.id.startsWith('btn-quickbuy-')) continue;
      btn.style.display = this.quickBuyVisible ? '' : 'none';
    }
  }

  /** MOB-W6: toggle the three quick-buy buttons together. Call from the
   *  host (game.ts) when the round phase enters/leaves freeze_time so the
   *  buttons only render when buying is meaningful. */
  setQuickBuyVisible(visible: boolean): void {
    this.quickBuyVisible = visible;
    this.applyQuickBuyVisibility();
  }

  /** MOB-W6: update the textContent of a quick-buy button so it reflects
   *  the user-chosen weapon. Slots are 0-2 to match QuickBuyController.
   *  Out-of-range slots are a no-op. */
  setQuickBuyLabel(slot: 0 | 1 | 2, label: string): void {
    const id = `btn-quickbuy-${slot + 1}`;
    for (const [btn] of this.buttonMap) {
      if (btn.id !== id) continue;
      btn.textContent = label;
      return;
    }
  }

  /** BUG-MT-7: register the rising-edge callback fired on the map-vote button
   *  press. Matches the setOn* pattern used elsewhere in the input layer. */
  setOnMapVote(cb: (() => void) | null): void {
    this.onMapVote = cb;
  }

  /** BUG-PARITY-H2: register the rising-edge callback fired on the radio
   *  button press. Mirrors setOnMapVote so the TouchInputProvider can route
   *  the press to the radio command wheel (desktop Z/X/C parity). */
  setOnRadio(cb: (() => void) | null): void {
    this.onRadio = cb;
  }

  /** BUG-MT-30: toggle the .ptt-transmitting class on #btn-ptt so the player
   *  gets clear visual feedback that their voice is going out. TouchInputProvider
   *  is the single caller (driven by the voiceChat speaking/silent VAD events).
   *  Idempotent — safe to call every frame. */
  setPTTTransmitting(active: boolean): void {
    for (const [btn] of this.buttonMap) {
      if (btn.id !== 'btn-ptt') continue;
      if (active) btn.classList?.add?.('ptt-transmitting');
      else btn.classList?.remove?.('ptt-transmitting');
      return;
    }
  }

  /** MOB-L4 / BUG-MT-24: a light haptic ping on press if supported.
   *  BUG-MT-24: respect the haptics enable/strength settings rather than
   *  unconditionally buzzing every press. The settings store currently
   *  only carries voice/graphics fields; we read haptics defensively so a
   *  future settings extension (settingsStore.haptics.{enabled,strength})
   *  is honored immediately without another touch-buttons edit. Wrapped
   *  in try/catch so an unsupported navigator.vibrate or a browser-blocked
   *  call is harmless. */
  private hapticPing(): void {
    try {
      let enabled = true;
      let strength = 1.0;
      try {
        // Defensive read — the haptics group is not yet declared in
        // GameSettings, so cast through unknown to avoid widening the
        // public type. When the settings shape is extended, this branch
        // picks up the new fields with zero churn here.
        const s = settingsStore.get() as unknown as {
          haptics?: { enabled?: boolean; strength?: number };
        };
        if (s.haptics) {
          if (typeof s.haptics.enabled === 'boolean') enabled = s.haptics.enabled;
          if (typeof s.haptics.strength === 'number') {
            strength = Math.max(0, Math.min(1, s.haptics.strength));
          }
        }
      } catch {
        // settings unavailable (e.g. stub-DOM tests) — fall back to defaults.
      }
      if (!enabled || strength <= 0) return;
      const nav = navigator as unknown as { vibrate?: (pattern: number | number[]) => boolean };
      if (typeof nav.vibrate === 'function') {
        // Default 8ms ping scaled by strength, clamped to at least 1ms so a
        // tiny non-zero strength still produces a perceptible tick.
        const duration = Math.max(1, Math.round(8 * strength));
        nav.vibrate(duration);
      }
    } catch {
      // ignore — vibrate is best-effort
    }
  }

  private onTouchStart(e: TouchEvent): void {
    e.preventDefault();
    const target = e.currentTarget as HTMLElement;
    const key = this.buttonMap.get(target);
    if (!key) return;

    for (let i = 0; i < e.changedTouches.length; i++) {
      const t = e.changedTouches[i];
      // BUG-MT-4: when two buttons fire in the same touchstart event (e.g. the
      // user lands two fingers simultaneously on FIRE + JUMP), each button's
      // listener sees both touches in changedTouches. Without filtering by
      // currentTarget, both listeners would overwrite each other's
      // identifier→key map, leaving one button "owned" by the wrong touch.
      if (t.target !== target) continue;
      this.activeTouches.set(t.identifier, key);
    }
    this.state[key] = true;

    // BUG-MT-7: fire the rising-edge map-vote callback on the press itself.
    // Routing through onTouchStart guarantees a single invocation per tap
    // (onTouchEnd would also fire if the user dragged off the button, which
    // is not the desired UX for a panel toggle).
    if (key === 'mapvote' && this.onMapVote) {
      try {
        this.onMapVote();
      } catch {
        // host callback errors must not break the input loop.
      }
    }

    // BUG-PARITY-H2: fire the rising-edge radio callback on the press itself
    // for the same reason as the map-vote dispatch above — a single tap should
    // open the radio command wheel exactly once.
    if (key === 'radio' && this.onRadio) {
      try {
        this.onRadio();
      } catch {
        // host callback errors must not break the input loop.
      }
    }

    // MOB-L4: pronounced press feedback — scale-down + brightness boost via
    // a CSS class, plus a haptic ping if the device supports it.
    target.classList?.add?.('touch-button-pressed');
    this.hapticPing();
  }

  private onTouchEnd(e: TouchEvent): void {
    const target = e.currentTarget as HTMLElement;
    for (let i = 0; i < e.changedTouches.length; i++) {
      const t = e.changedTouches[i];
      // BUG-MT-4: same multi-touch filter as onTouchStart — only handle the
      // changedTouches whose original target is this button. Without this,
      // releasing two fingers in the same touchend would invoke both buttons'
      // listeners against the union of touches and could clear state on the
      // wrong button.
      if (t.target !== target) continue;
      const id = t.identifier;
      const key = this.activeTouches.get(id);
      if (key) {
        this.activeTouches.delete(id);
        // Only release if no other touch is holding the same button
        let stillHeld = false;
        for (const [, k] of this.activeTouches) {
          if (k === key) { stillHeld = true; break; }
        }
        if (!stillHeld) {
          this.state[key] = false;
          // MOB-L4: clear the pressed visual on the matching button.
          for (const [btn, k] of this.buttonMap) {
            if (k === key) btn.classList?.remove?.('touch-button-pressed');
          }
        }
      }
    }
  }

  /** Clear all active touches and reset button states (e.g. on visibility change). */
  private clearAllTouches(): void {
    this.activeTouches.clear();
    this.state.fire = false;
    this.state.ads = false;
    this.state.reload = false;
    this.state.jump = false;
    this.state.crouch = false;
    this.state.weaponSwitch = false;
    this.state.ptt = false;
    this.state.scope = false;
    this.state.interact = false;
    this.state.buy = false;
    this.state.scoreboard = false;
    this.state.grenade = false;
    this.state.ping = false;
    this.state.chat = false;
    this.state.quickbuy1 = false;
    this.state.quickbuy2 = false;
    this.state.quickbuy3 = false;
    this.state.mapvote = false;
    this.state.radio = false;
    // MOB-L4: visual press state must clear in lockstep with input state so a
    // button doesn't stay "stuck pressed" when the tab loses focus.
    for (const [btn] of this.buttonMap) {
      btn.classList?.remove?.('touch-button-pressed');
    }
  }

  /** F-MOB-M3: hide advanced-only buttons and (optionally) enlarge core buttons
   *  in simple mode. Simple mode shows: FIRE / ADS / RELOAD / JUMP / CROUCH only.
   *  Advanced mode shows the full set.
   *
   *  BUG-MT-16: simple mode used to enlarge unconditionally and advanced-mode
   *  restore only triggered when the existing size matched the BUTTON_DEFS
   *  default. After simple→advanced, the user's HUD-editor customization
   *  was silently replaced by `def.size`. We now snapshot the current per-
   *  button px right before enlarging into simple mode, and restore from
   *  that cache on the way back. If the cache is empty (first call ever),
   *  we still respect a HUD-editor-saved size by leaving non-default px
   *  values alone — the previous behavior. */
  applyControlMode(mode: ControlMode): void {
    if (!this.container) return;
    // BUG-MT-16: capture user-customized sizes on the transition into simple
    // mode, before the enlargement loop overwrites them. We snapshot every
    // button's current width regardless of mode/visibility because the
    // restore path needs the original value to bounce back to.
    if (mode === 'simple') {
      for (const [btn] of this.buttonMap) {
        const current = btn.style.width;
        if (!current) continue;
        const m = /^([\d.]+)px$/.exec(current);
        if (!m) continue;
        const px = parseFloat(m[1]);
        if (!Number.isFinite(px)) continue;
        // Only cache if we haven't already snapshotted — repeated calls to
        // applyControlMode('simple') would otherwise overwrite the user
        // value with the enlarged 60px placeholder.
        if (!this.userCustomSizes.has(btn.id)) {
          this.userCustomSizes.set(btn.id, px);
        }
      }
    }

    for (const [btn, key] of this.buttonMap) {
      const def = BUTTON_DEFS.find((d) => d.key === key);
      const allowedModes = def?.modes ?? ['simple', 'advanced'];
      const visible = allowedModes.includes(mode === 'custom' ? 'advanced' : mode);
      // F-MOB-H3 (bomb): the bomb button has an extra gate on top of the
      // mode filter — even in advanced mode it stays hidden unless the local
      // player is the carrier. Defer to applyBombVisibility() after the loop.
      if (btn.id === 'btn-bomb') {
        // skip — applyBombVisibility() is the single owner of #btn-bomb display
        continue;
      }
      // MOB-W6: same gate for quick-buy buttons — applyQuickBuyVisibility()
      // is the single owner of their display style based on the round
      // phase, not the control mode.
      if (btn.id.startsWith('btn-quickbuy-')) {
        continue;
      }
      btn.style.display = visible ? '' : 'none';

      if (visible && def) {
        if (mode === 'simple') {
          // Larger hit areas in simple mode for the core five buttons.
          const targetSize = Math.max(def.size, 60);
          btn.style.width = `${targetSize}px`;
          btn.style.height = `${targetSize}px`;
        } else {
          // BUG-MT-16: restore the user's pre-simple-mode size if we have one,
          // otherwise fall back to the previous behavior (only reset default
          // sizes; leave HUD-editor overrides alone).
          const cached = this.userCustomSizes.get(btn.id);
          if (cached !== undefined) {
            btn.style.width = `${cached}px`;
            btn.style.height = `${cached}px`;
            this.userCustomSizes.delete(btn.id);
          } else {
            const current = btn.style.width;
            if (!current || current === `${def.size}px`) {
              btn.style.width = `${def.size}px`;
              btn.style.height = `${def.size}px`;
            }
          }
        }
      }

      // Reset button input state if the button is hidden — otherwise a button
      // that was held down at the moment the user switched modes would stay
      // stuck-pressed forever.
      if (!visible) {
        this.state[key] = false;
        btn.classList?.remove?.('touch-button-pressed');
      }
    }
    // F-MOB-H3 (bomb): re-apply bomb visibility after the mode-driven loop so
    // the carrier-only gating wins regardless of the active control mode.
    this.applyBombVisibility();
    // MOB-W6: same dance for the quick-buy buttons.
    this.applyQuickBuyVisibility();
  }

  /** Get current button pressed states. */
  getState(): TouchButtonState {
    return { ...this.state };
  }

  /** Show the button overlay. */
  show(): void {
    this.applyVisibility(true);
  }

  /** Hide the button overlay. */
  hide(): void {
    this.applyVisibility(false);
  }

  /** BUG-MT-27: single-paint visibility commit. The first show()/hide() call
   *  after construction defers via requestAnimationFrame so the initial
   *  visibility lands in the same paint as the freshly-mounted DOM,
   *  preventing the "visible then hidden" flash users see on first join.
   *  Subsequent calls apply synchronously. The hide-side clearAllTouches()
   *  flush (BUG-MT-17) still runs immediately so stuck input state cannot
   *  survive across an rAF boundary. */
  private applyVisibility(visible: boolean): void {
    const apply = (): void => {
      if (this.container) {
        this.container.style.display = visible ? '' : 'none';
      }
      if (!visible) {
        // BUG-MT-17: setting display:none does NOT fire touchend on held
        // buttons, so any in-flight presses would leave entries in
        // activeTouches and stuck `true` state.* values that would survive
        // the next show(). Flush them now so the next time the overlay
        // returns visible all buttons start clean.
        this.clearAllTouches();
      }
    };
    if (!this.firstVisibilityApplied) {
      this.firstVisibilityApplied = true;
      // Defer the first commit so the initial visibility lands in one paint
      // with the freshly-mounted container. Fall back to a synchronous
      // apply when requestAnimationFrame is unavailable (vitest stub-DOMs).
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(apply);
        return;
      }
    }
    apply();
  }

  /** Remove all DOM elements and listeners. */
  dispose(): void {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    // BUG-MT-19: matching teardown for the pagehide listener wired in the ctor.
    if (typeof window !== 'undefined' && typeof window.removeEventListener === 'function') {
      window.removeEventListener('pagehide', this.handlePageHide);
    }
    if (this.container) {
      // Remove event listeners from buttons
      for (const [btn] of this.buttonMap) {
        btn.removeEventListener('touchstart', this.handleStart);
        btn.removeEventListener('touchend', this.handleEnd);
        btn.removeEventListener('touchcancel', this.handleEnd);
      }
      this.container.remove();
      this.container = null;
    }
    this.buttonMap.clear();
    this.activeTouches.clear();
  }
}
